import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const RestaurantContext = createContext(null);

export function RestaurantProvider({ children }) {
  const { api, user } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRestaurants = useCallback(async () => {
    if (!api || !user) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/my-restaurants');
      setRestaurants(response.data);
      
      // Auto-select first restaurant if available OR update existing selection
      if (response.data.length > 0) {
        if (!selectedRestaurant) {
          setSelectedRestaurant(response.data[0]);
        } else {
          // Update the selected restaurant with fresh data
          const updatedSelected = response.data.find(r => r.id === selectedRestaurant.id);
          if (updatedSelected) {
            setSelectedRestaurant(updatedSelected);
          } else {
            setSelectedRestaurant(response.data[0]);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch restaurants:', err);
      setError(err.response?.data?.detail || 'Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  // Create a new restaurant
  const createRestaurant = async (data) => {
    try {
      const response = await api.post('/restaurants', data);
      // Refresh the list
      await fetchRestaurants();
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  // Update restaurant
  const updateRestaurant = async (restaurantId, data) => {
    try {
      await api.put(`/restaurants/${restaurantId}`, data);
      await fetchRestaurants();
    } catch (err) {
      throw err;
    }
  };

  const value = {
    restaurants,
    selectedRestaurant,
    setSelectedRestaurant,
    loading,
    error,
    hasRestaurant: restaurants.length > 0,
    createRestaurant,
    updateRestaurant,
    refreshRestaurants: fetchRestaurants
  };

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
}

export function useRestaurant() {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error('useRestaurant must be used within a RestaurantProvider');
  }
  return context;
}
